import React from 'react';
import { X } from 'lucide-react';

interface FormulaExamplesModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableFields?: Array<{ label: string; type: string }>;
}

const FormulaExamplesModal: React.FC<FormulaExamplesModalProps> = ({ 
  isOpen, 
  onClose,
  availableFields = []
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4">
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Formula Examples & Guide</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Quick Start */}
          <section>
            <h3 className="font-semibold text-gray-900 mb-2 text-lg">Quick Start</h3>
            <p className="text-sm text-gray-700 mb-3">
              Formulas reference other fields by their exact label. Use quotes for text values.
            </p>
            <div className="bg-gray-50 p-3 rounded border font-mono text-sm">
              IF(Current Inventory &lt; Par Level, "Below Par", "OK")
            </div>
          </section>

          {/* Common Patterns */}
          <section>
            <h3 className="font-semibold text-gray-900 mb-3 text-lg">Common Patterns</h3>
            
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-800 mb-2">Status Check</p>
                <div className="bg-gray-50 p-3 rounded border font-mono text-xs mb-2">
                  IF(Current Inventory &lt; Par Level, "Below Par", "OK")
                </div>
                <p className="text-xs text-gray-600">
                  Returns "Below Par" if current inventory is below par level, otherwise "OK"
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-800 mb-2">Calculate Difference</p>
                <div className="bg-gray-50 p-3 rounded border font-mono text-xs mb-2">
                  Par Level - Current Inventory
                </div>
                <p className="text-xs text-gray-600">
                  Subtracts current from par level (result can be negative if over stock)
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-800 mb-2">Prevent Negative</p>
                <div className="bg-gray-50 p-3 rounded border font-mono text-xs mb-2">
                  MAX(0, Par Level - Current Inventory)
                </div>
                <p className="text-xs text-gray-600">
                  Reorder quantity that never goes negative (minimum value is 0)
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-800 mb-2">Percentage</p>
                <div className="bg-gray-50 p-3 rounded border font-mono text-xs mb-2">
                  (Current Inventory / Par Level) * 100
                </div>
                <p className="text-xs text-gray-600">
                  Stock level as percentage of par level (e.g., 56 for 56%)
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-800 mb-2">Range Check (3-way)</p>
                <div className="bg-gray-50 p-3 rounded border font-mono text-xs mb-2">
                  IF(Current Temperature &lt; Min Temperature, "Too Cold", IF(Current Temperature &gt; Max Temperature, "Too Hot", "OK"))
                </div>
                <p className="text-xs text-gray-600">
                  Nested IF for three-way decision: too cold, too hot, or OK
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-800 mb-2">Pass/Fail</p>
                <div className="bg-gray-50 p-3 rounded border font-mono text-xs mb-2">
                  IF(Defects Found &lt;= Max Defects, "Pass", "Fail")
                </div>
                <p className="text-xs text-gray-600">
                  Quality check: Pass if defects are within limit, otherwise Fail
                </p>
              </div>
            </div>
          </section>

          {/* Operators Reference */}
          <section>
            <h3 className="font-semibold text-gray-900 mb-3 text-lg">Operators</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="border border-gray-200 rounded p-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">+</code>
                <span className="ml-2 text-gray-700">Add</span>
              </div>
              <div className="border border-gray-200 rounded p-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">-</code>
                <span className="ml-2 text-gray-700">Subtract</span>
              </div>
              <div className="border border-gray-200 rounded p-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">*</code>
                <span className="ml-2 text-gray-700">Multiply</span>
              </div>
              <div className="border border-gray-200 rounded p-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">/</code>
                <span className="ml-2 text-gray-700">Divide</span>
              </div>
              <div className="border border-gray-200 rounded p-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">&lt;</code>
                <span className="ml-2 text-gray-700">Less than</span>
              </div>
              <div className="border border-gray-200 rounded p-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">&gt;</code>
                <span className="ml-2 text-gray-700">Greater than</span>
              </div>
              <div className="border border-gray-200 rounded p-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">&lt;=</code>
                <span className="ml-2 text-gray-700">Less or equal</span>
              </div>
              <div className="border border-gray-200 rounded p-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">&gt;=</code>
                <span className="ml-2 text-gray-700">Greater or equal</span>
              </div>
              <div className="border border-gray-200 rounded p-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">==</code>
                <span className="ml-2 text-gray-700">Equal</span>
              </div>
              <div className="border border-gray-200 rounded p-2">
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">!=</code>
                <span className="ml-2 text-gray-700">Not equal</span>
              </div>
            </div>
          </section>

          {/* Functions Reference */}
          <section>
            <h3 className="font-semibold text-gray-900 mb-3 text-lg">Functions</h3>
            <div className="space-y-3">
              <div className="border border-gray-200 rounded-lg p-3">
                <code className="bg-gray-100 px-2 py-1 rounded text-sm">IF(condition, trueValue, falseValue)</code>
                <p className="text-xs text-gray-600 mt-2 ml-4">
                  Conditional: Returns trueValue if condition is true, otherwise falseValue.
                  <br />
                  <span className="font-mono text-xs">Example: IF(A &lt; 10, "Low", "OK")</span>
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-3">
                <code className="bg-gray-100 px-2 py-1 rounded text-sm">MAX(a, b, ...)</code>
                <p className="text-xs text-gray-600 mt-2 ml-4">
                  Returns the maximum value from arguments.
                  <br />
                  <span className="font-mono text-xs">Example: MAX(0, A - B)</span>
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-3">
                <code className="bg-gray-100 px-2 py-1 rounded text-sm">MIN(a, b, ...)</code>
                <p className="text-xs text-gray-600 mt-2 ml-4">
                  Returns the minimum value from arguments.
                  <br />
                  <span className="font-mono text-xs">Example: MIN(A, B)</span>
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-3">
                <code className="bg-gray-100 px-2 py-1 rounded text-sm">ABS(x)</code>
                <p className="text-xs text-gray-600 mt-2 ml-4">
                  Returns absolute value (removes negative sign).
                  <br />
                  <span className="font-mono text-xs">Example: ABS(A - B)</span>
                </p>
              </div>
            </div>
          </section>

          {/* Tips */}
          <section className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-900 mb-2 text-lg">💡 Tips</h3>
            <ul className="text-sm text-yellow-800 space-y-1 list-disc ml-5">
              <li>Field labels are case-sensitive - use exact label text</li>
              <li>Text values must be in quotes: "Below Par" not Below Par</li>
              <li>Referenced fields must exist and come before this calculated field</li>
              <li>Use parentheses to control calculation order: (A + B) * C</li>
              <li>Test your formula with sample values before saving</li>
            </ul>
          </section>

          {/* Available Fields */}
          {availableFields.length > 0 && (
            <section>
              <h3 className="font-semibold text-gray-900 mb-2 text-lg">Available Fields in This Form</h3>
              <div className="flex flex-wrap gap-2">
                {availableFields.map(field => (
                  <span
                    key={field.label}
                    className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-mono"
                  >
                    "{field.label}"
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-600 mt-2">
                Use these exact labels in your formulas. Fields must come before this calculated field in the form order.
              </p>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormulaExamplesModal;

